import { Inject, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { ORDER_BUSINESS_CONFIG_TOKEN } from '../../core/tokens';
import {
  DurationInterface,
  DurationPeriod,
  Order,
  SalesOrdersService,
  Source,
  Status,
  StatusHistoryItemInterface,
  UserRole,
} from '@vendasta/sales-orders';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  withLatestFrom,
} from 'rxjs/operators';
import { LineItemsService } from '../line-items/line-items.service';
import { PARTNER_ID } from '../../shared/constants';
import { OrderStoreService } from '../../core/orders.service';
import { Salesperson, SalespersonService } from '@vendasta/sales';
import { AccountGroup } from '@galaxy/account-group';
import { OrderFeature, OrderPermissionsService } from '../../core/permissions';
import { DatePipe } from '@angular/common';
import { OrderAction } from '../../core/permissions/permissions';

export interface OrderDetails {
  loading: boolean;
  canEditContractDetails: boolean;
  accountLink: string;
  companyLink: string;
  business: AccountGroup;
  salesperson?: Salesperson;
  orderUserDetails: OrderUserDetails;
  contractDetails: ContractDetails;
  customerAgreementData?: CustomerAgreementData;
}

export interface ContractDetails {
  contractStartDate: Date;
  minimumContractStartDate: Date;
  contractDuration: DurationInterface;
}

export interface OrderUserDetails {
  badgeColor: string;
  badgeTranslationKey: string;
  name: string;
}

interface CustomerAgreementData {
  lastAcceptedName: string;
  lastAcceptedDate: string;
  agreements: CustomerAgreement[];
}

interface CustomerAgreement {
  name: string;
  term: string;
  agreed: boolean;
}

export const DEFAULT_CONTRACT_DURATION: DurationInterface = {
  value: 1,
  duration: DurationPeriod.YEAR,
};

type UserInfo = { name: string; role: UserRole; source: Source };

const userRoleToBadge = {
  [UserRole.USER_ROLE_ADMIN]: { color: 'blue', translationKey: 'FRONTEND.SALES_UI.ADMIN' },
  [UserRole.USER_ROLE_SALESPERSON]: { color: 'purple', translationKey: 'FRONTEND.SALES_UI.SALESPERSON' },
  [UserRole.USER_ROLE_CLIENT]: { color: 'green', translationKey: 'FRONTEND.SALES_UI.CLIENT' },
};

const userSourceToDisplayName = {
  [Source.SOURCE_SYSTEM]: 'LIB_ORDERS.COMMON.ORDER_DETAILS.SYSTEM',
  [Source.SOURCE_SYSTEM_ADMIN]: 'LIB_ORDERS.COMMON.ORDER_DETAILS.SYSTEM_ADMIN',
  [Source.SOURCE_INVALID]: undefined,
};

const setContractStartDateFn =
  (input: { loading: boolean; startDate: Date }) =>
  (orderDetails: OrderDetails): OrderDetails => {
    orderDetails.loading = input.loading;
    orderDetails.contractDetails = {
      ...orderDetails?.contractDetails,
      contractStartDate: input.startDate,
    };
    return orderDetails;
  };

const setContractDurationFn =
  (input: { loading: boolean; contractDuration: DurationInterface }) =>
  (orderDetails: OrderDetails): OrderDetails => {
    orderDetails.loading = input.loading;
    orderDetails.contractDetails = {
      ...orderDetails?.contractDetails,
      contractDuration: { ...input.contractDuration },
    };
    return orderDetails;
  };

const setInitialDataFn = (input: OrderDetails) => (_: OrderDetails) => input;

@Injectable()
export class CondensedOrderDetailsService {
  private readonly _updateContractStartDate$$ = new BehaviorSubject<Date | null>(null);
  private readonly _updateContractDuration$$ = new Subject<DurationInterface>();

  public readonly orderDetails$: Observable<OrderDetails>;

  private readonly orderBusinessConfig = inject(ORDER_BUSINESS_CONFIG_TOKEN, { optional: true });
  private readonly loading$$ = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loading$$.asObservable();
  private readonly canEditContractDetails$ = this.permissionsService.CanEdit(OrderFeature.ContractStartAndDuration);
  private readonly canUpdateInvalidContractStartDate$ = this.permissionsService.CanDoAction(
    OrderAction.UpdateInvalidContractStartDate,
  );

  constructor(
    @Inject(PARTNER_ID) private readonly partnerId$: Observable<string>,
    private readonly orderService: OrderStoreService,
    private salesOrdersService: SalesOrdersService,
    private lineItemsService: LineItemsService,
    private readonly snack: SnackbarService,
    private readonly salespersonService: SalespersonService,
    private readonly permissionsService: OrderPermissionsService,
    private readonly translateService: TranslateService,
  ) {
    const updateContractStartDate$ = this._updateContractStartDate$$.asObservable().pipe(
      filter((date): date is Date => date !== null),
      distinctUntilChanged((prev, curr) => prev.getTime() === curr.getTime()),
      withLatestFrom(this.orderService.order$),
      switchMap(([newContractStartDate, order]) =>
        this._buildUpdateContractStartDateStream(order.orderId, order.businessId, newContractStartDate),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const updateContractDuration$ = this._updateContractDuration$$.asObservable().pipe(
      withLatestFrom(this.orderService.order$),
      switchMap(([newContractDuration, order]) =>
        this._buildUpdateContractDurationStream(order.orderId, order.businessId, newContractDuration),
      ),
    );

    const accountLink$ = this.getAccountLink();
    const companyLink$ = this.getCompanyLink();
    const business$ = this.getBusiness();
    const salesperson$: Observable<Pick<OrderDetails, 'salesperson'>> = combineLatest([
      this.partnerId$,
      this.orderService.order$,
      business$,
    ]).pipe(
      switchMap(([partnerId, order, { business }]: [string, Order, Pick<OrderDetails, 'business'>]) =>
        this.getSalesperson(partnerId, order, business),
      ),
    );

    const orderUserDetails$ = this.orderService.order$.pipe(
      map((order) => this.getOrderCreatorUser(order?.statusHistory)),
    );

    const contractStartDate$ = combineLatest([this.orderService.order$, this.canUpdateInvalidContractStartDate$]).pipe(
      map(([order, canUpdateInvalidContractStartDate]) =>
        this.validateContractStartDate(order?.requestedActivation, canUpdateInvalidContractStartDate),
      ),
    );
    const contractDuration$ = this.orderService.order$.pipe(map((order) => order.contractDuration));

    const customerAgreementData$ = this.orderService.order$.pipe(
      map((order: Order) => {
        return { order, userMap: this.getStatusHistoryUsersMapFromOrder(order) };
      }),
      map(({ order, userMap }) => {
        const agreements = this.constructCustomerAgreements(order, userMap);
        const agreementUser = this.getLastAcceptedUser(order.statusHistory, userMap);
        return {
          lastAcceptedName: agreementUser.name,
          lastAcceptedDate: agreementUser.date,
          agreements: agreements,
        };
      }),
    );

    const initialData$ = combineLatest([
      accountLink$,
      companyLink$,
      business$,
      this.canEditContractDetails$,
      salesperson$,
      orderUserDetails$,
      contractStartDate$,
      contractDuration$,
      customerAgreementData$,
    ]).pipe(
      map(
        ([
          accountLinkData,
          companyLinkData,
          businessData,
          canEditContractDetails,
          salespersonData,
          orderUserDetailsData,
          contractStartDate,
          contractDuration,
          customerAgreementData,
        ]) => {
          return {
            loading: false,
            accountLink: accountLinkData?.accountLink,
            companyLink: companyLinkData?.companyLink,
            business: businessData?.business,
            canEditContractDetails: canEditContractDetails,
            salesperson: salespersonData?.salesperson,
            orderUserDetails: orderUserDetailsData?.orderUserDetails,
            contractDetails: {
              contractStartDate: contractStartDate,
              minimumContractStartDate: new Date(new Date().setHours(7, 0, 0, 0)),
              contractDuration: {
                value: contractDuration?.value || DEFAULT_CONTRACT_DURATION.value,
                duration: contractDuration?.duration || DEFAULT_CONTRACT_DURATION.duration,
              },
            },
            customerAgreementData: customerAgreementData,
          };
        },
      ),
      startWith({ loading: true }),
    );

    this.orderDetails$ = merge(
      initialData$.pipe(map(setInitialDataFn)),
      updateContractStartDate$.pipe(map(setContractStartDateFn)),
      updateContractDuration$.pipe(map(setContractDurationFn)),
    ).pipe(
      scan((acc, fn) => fn({ ...acc }), {
        loading: false,
        accountLink: '',
        companyLink: '',
        canEditContractDetails: false,
        business: null,
        salesperson: null,
        orderUserDetails: null,
        contractDetails: null,
        customerAgreementData: null,
      }),
      distinctUntilChanged((prev, curr) => this.areOrderDetailsEqual(prev as OrderDetails, curr as OrderDetails)),
      shareReplay(1),
    );
  }

  areOrderDetailsEqual(prev: OrderDetails, curr: OrderDetails): boolean {
    return (
      prev.loading === curr.loading &&
      prev.accountLink === curr.accountLink &&
      prev.companyLink === curr.companyLink &&
      prev.business === curr.business &&
      prev.salesperson === curr.salesperson &&
      prev.orderUserDetails?.name === curr.orderUserDetails?.name &&
      prev.orderUserDetails?.badgeColor === curr.orderUserDetails?.badgeColor &&
      prev.orderUserDetails?.badgeTranslationKey === curr.orderUserDetails?.badgeTranslationKey &&
      prev.contractDetails?.contractStartDate === curr.contractDetails?.contractStartDate &&
      prev.contractDetails?.contractDuration?.value === curr.contractDetails?.contractDuration?.value &&
      prev.contractDetails?.contractDuration?.duration === curr.contractDetails?.contractDuration?.duration &&
      prev.customerAgreementData?.lastAcceptedDate === curr.customerAgreementData?.lastAcceptedDate &&
      prev.customerAgreementData?.lastAcceptedName === curr.customerAgreementData?.lastAcceptedName &&
      curr.customerAgreementData?.agreements.every((agreement, index) => {
        return (
          agreement?.name === curr?.customerAgreementData?.agreements?.[index]?.name &&
          agreement?.term === curr?.customerAgreementData?.agreements?.[index]?.term &&
          agreement?.agreed === curr?.customerAgreementData?.agreements?.[index]?.agreed
        );
      })
    );
  }

  // Get the CRM company link
  getCompanyLink(): Observable<Pick<OrderDetails, 'companyLink'>> {
    return this.orderService.order$.pipe(
      switchMap((order: Order) => {
        const url = this.orderBusinessConfig?.getBusinessUrl?.(order.businessId) ?? `/info/${order.businessId}`;
        if (url instanceof Promise) {
          return from(url);
        }
        return of(url);
      }),
      map((url) => ({ companyLink: url })),
    );
  }

  // Get link to the account
  getAccountLink(): Observable<Pick<OrderDetails, 'accountLink'>> {
    return this.orderService.order$.pipe(
      switchMap((order: Order) => {
        const url = `/businesses/accounts/${order.businessId}/details`;
        return of(url);
      }),
      map((url) => ({ accountLink: url })),
    );
  }

  getBusiness(): Observable<Pick<OrderDetails, 'business'>> {
    return this.orderService.business$.pipe(map((business) => ({ business })));
  }

  private _buildUpdateContractStartDateStream(
    orderId: string,
    businessId: string,
    contractStartDate: Date,
  ): Observable<{ loading: boolean; startDate: Date }> {
    contractStartDate.setHours(7);
    return this.salesOrdersService.updateRequestedActivation(orderId, businessId, contractStartDate).pipe(
      map(() => {
        this.lineItemsService.setContractStartDate(contractStartDate);
        this.orderService.reloadOrder();
        return { loading: false, startDate: contractStartDate };
      }),
      startWith({ loading: true, startDate: contractStartDate }),
    );
  }

  private _buildUpdateContractDurationStream(
    orderId: string,
    businessId: string,
    contractDuration: DurationInterface,
  ): Observable<{ loading: boolean; contractDuration: DurationInterface }> {
    return this.salesOrdersService.updateContractDuration(orderId, businessId, contractDuration).pipe(
      map(() => {
        this.lineItemsService.setContractDuration(contractDuration);
        return { loading: false, contractDuration: contractDuration };
      }),
      startWith({ loading: true, contractDuration: contractDuration }),
    );
  }

  public updateContractStartDate(newContractStartDate: Date): void {
    this._updateContractStartDate$$.next(newContractStartDate);
  }

  public updateContractDuration(newDuration: DurationInterface): void {
    this._updateContractDuration$$.next(newDuration);
  }

  private getSalesperson(
    partnerId: string,
    order: Order,
    business: AccountGroup,
  ): Observable<Pick<OrderDetails, 'salesperson'>> {
    let salespersonId = '';

    // Use the salespersonId on the order if there is one
    if (order?.salespersonId && order.salespersonId !== 'DEFAULT') {
      salespersonId = order.salespersonId;
      // Otherwise use the salespersonId assigned to the business
    } else if (business?.externalIdentifiers?.salesPersonId) {
      salespersonId = business.externalIdentifiers.salesPersonId;
    }

    if (salespersonId) {
      if (salespersonId.startsWith('U-')) {
        return this.salespersonService.getSalespersonByUserId(partnerId, salespersonId).pipe(
          map((salesperson) => ({ salesperson })),
          catchError((error) => {
            console.error('Error fetching salesperson by User ID:', error);
            return of({ salesperson: null });
          }),
        );
      } else if (salespersonId.startsWith('UID-')) {
        return this.salespersonService.getSalespersonBySubjectId(partnerId, salespersonId).pipe(
          map((salesperson) => ({ salesperson })),
          catchError((error) => {
            console.error('Error fetching salesperson by Subject ID:', error);
            return of({ salesperson: null });
          }),
        );
      }
    }

    return of({ salesperson: null });
  }

  private getUserNameOrSource(name: string, source: Source): string {
    if (name) {
      return name;
    } else if (source) {
      return this.translateService.instant(userSourceToDisplayName[source]);
    }
    return undefined;
  }

  private getOrderCreatorUser(orderHistory: StatusHistoryItemInterface[]) {
    if (orderHistory?.length > 0) {
      const orderCreatorUser = orderHistory[0];
      if (orderCreatorUser?.name?.length > 0 || !!orderCreatorUser?.source) {
        const userBadge = userRoleToBadge?.[orderCreatorUser.userRole];
        return {
          orderUserDetails: {
            name: this.getUserNameOrSource(orderCreatorUser?.name, orderCreatorUser?.source),
            badgeColor: userBadge?.color,
            badgeTranslationKey: userBadge?.translationKey,
          },
        };
      }
    }
    return { orderUserDetails: undefined };
  }

  public getStatusHistoryUsersMapFromOrder(order: Order): { [userId: string]: UserInfo } {
    const userInfoMap: { [userId: string]: UserInfo } = {};
    if (order?.statusHistory?.length > 0) {
      order.statusHistory.forEach((historyItem) => {
        if (historyItem?.userId && !userInfoMap[historyItem.userId]) {
          userInfoMap[historyItem.userId] = {
            name: historyItem.name,
            role: historyItem.userRole,
            source: historyItem.source,
          };
        }
      });
    }

    return userInfoMap;
  }

  private constructCustomerAgreements(order: Order, userMap: { [userId: string]: UserInfo }) {
    return (order?.agreements || []).map((agreement) => {
      const user = userMap[agreement?.userId];
      return {
        name: this.getUserNameOrSource(user?.name, user?.source),
        term: agreement?.term,
        agreed: agreement?.agreed,
      } as CustomerAgreement;
    });
  }

  public getLastAcceptedUser(orderHistory: StatusHistoryItemInterface[], userMap: { [userId: string]: UserInfo }) {
    for (let i = orderHistory?.length - 1; i > 0; i--) {
      if (
        orderHistory[i].status == Status.SUBMITTED_FOR_CUSTOMER_APPROVAL &&
        orderHistory[i + 1] &&
        (!orderHistory[i + 1]?.status || orderHistory[i + 1]?.status == Status.AWAITING_PAYMENT)
      ) {
        const user = userMap[orderHistory[i + 1].userId];
        return {
          name: this.getUserNameOrSource(user?.name, user?.source),
          date: new DatePipe('en-US').transform(orderHistory[i + 1].created, 'MMMM d, y, h:mm:ss a'),
        };
      }
    }
    return { name: '', date: '' };
  }

  public validateContractStartDate(contractStartDay: Date, canUpdateInvalidContractStartDate: boolean): Date {
    const earliestContractStartDay = new Date(new Date().setHours(7, 0, 0, 0));
    if (contractStartDay < earliestContractStartDay && canUpdateInvalidContractStartDate) {
      this.updateContractStartDate(earliestContractStartDay);
      contractStartDay = earliestContractStartDay;
    }
    return contractStartDay;
  }
}
